<template>
  <!-- BUTTON GENERIC -->
  <div
    v-if="!dropdown"
    class="mg-btn-root"
    :data-bs-toggle="onHoverText ? 'tooltip' : undefined"
    :data-bs-placement="onHoverText ? 'top' : undefined"
    :title="onHoverText || undefined">
    <button :type="type" class="mg-btn" :class="btnClassList" :disabled="disabled || loading" @click="handleClick">
      <span class="mg-btn-face">
        <template v-if="loading">
          <div
            class="spinner-border spinner-border-sm mg-btn-spinner"
            :aria-hidden="label ? 'true' : undefined"
            :role="label ? undefined : 'status'" />
          <span v-if="label" class="mg-btn-label ms-2">{{ label }}</span>
        </template>
        <template v-else>
          <slot>
            <CustomIcon
              v-if="prefixIcon"
              class="mg-btn-icon mg-btn-icon--prefix"
              :class="{ 'me-2': label || suffixIcon }"
              :icon="prefixIcon"
              size="16px" />
            <slot v-else name="prefix-icon" />
            <span v-if="label" class="mg-btn-label">{{ label }}</span>
            <CustomIcon v-if="suffixIcon" class="mg-btn-icon mg-btn-icon--suffix ms-2" :icon="suffixIcon" size="16px" />
            <slot v-else name="suffix-icon" />
          </slot>
        </template>
      </span>
    </button>
  </div>

  <!-- BUTTON DROPDOWN -->
  <button
    v-else
    :type="type"
    class="mg-btn"
    :class="btnClassList"
    data-bs-toggle="dropdown"
    :disabled="disabled || loading"
    @click="handleClick">
    <span class="mg-btn-face">
      <template v-if="loading">
        <div
          class="spinner-border spinner-border-sm mg-btn-spinner"
          :aria-hidden="label ? 'true' : undefined"
          :role="label ? undefined : 'status'" />
        <span v-if="label" class="mg-btn-label ms-2">{{ label }}</span>
      </template>
      <template v-else>
        <slot>
          <CustomIcon
            v-if="prefixIcon"
            class="mg-btn-icon mg-btn-icon--prefix"
            :class="{ 'me-2': label || suffixIcon }"
            :icon="prefixIcon"
            size="16px" />
          <slot v-else name="prefix-icon" />
          <span v-if="label" class="mg-btn-label">{{ label }}</span>
          <CustomIcon v-if="suffixIcon" class="mg-btn-icon mg-btn-icon--suffix ms-2" :icon="suffixIcon" size="16px" />
          <slot v-else name="suffix-icon" />
        </slot>
      </template>
    </span>
  </button>
</template>

<script setup>
  import { computed, onMounted } from 'vue';
  import { Tooltip } from 'bootstrap';
  import CustomIcon from '@/components/CustomIcon.vue';

  const props = defineProps({
    label: {
      type: String,
      default: '',
    },
    primary: {
      type: Boolean,
      default: false,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    tertiary: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'medium',
    },
    additionalClass: {
      type: String,
      default: '',
    },
    onHoverText: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'button',
    },
    prefixIcon: {
      type: String,
      default: '',
    },
    suffixIcon: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    dropdown: {
      type: Boolean,
      default: false,
    },
  });

  const isIconOnly = computed(() => {
    if (props.label) return false;
    if (props.loading) return true;
    return (props.prefixIcon && !props.suffixIcon) || (!props.prefixIcon && props.suffixIcon);
  });

  const btnVariantClass = computed(() => {
    if (props.tertiary) return 'mg-btn-tertiary';
    if (props.secondary) return 'mg-btn-secondary';
    return 'mg-btn-primary';
  });

  const btnClassList = computed(() => [
    btnVariantClass.value,
    props.additionalClass,
    { 'mg-btn--icon-only': isIconOnly.value },
  ]);

  const emit = defineEmits(['click']);

  function handleClick(event) {
    event.stopPropagation();
    emit('click', event);
  }

  onMounted(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    [...tooltipTriggerList].map(el => new Tooltip(el));
  });
</script>

<style scoped lang="scss">
  .mg-btn-root.w-100 .mg-btn {
    width: 100%;
  }

  button.mg-btn.w-100 {
    width: 100%;
    max-width: 100%;
  }

  .mg-btn {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: auto;
    max-width: 100%;
    height: 40px;
    padding: 0 20px;
    margin: 0;
    cursor: pointer;
    transition: all 0.3s ease;
    background: transparent;
    box-shadow: none;
    font-size: 16px !important;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .mg-btn-face {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .mg-btn-label {
    flex-shrink: 0;
  }

  .mg-btn-icon {
    flex-shrink: 0;
  }

  .mg-btn-spinner {
    flex-shrink: 0;
  }

  .mg-btn-primary {
    background-color: var(--mg-primary-button-background-color);
    color: var(--mg-primary-button-text-color);
    border: calc(var(--mg-primary-button-border) * 1px) solid var(--mg-primary-button-text-color);
    border-radius: calc(var(--mg-primary-button-border-radius) * 1px);
    font-weight: var(--mg-primary-button-font-weight);

    &:hover:not(:disabled) {
      filter: none;
      background-color: var(--mg-primary-button-text-color);
      color: var(--mg-primary-button-background-color) !important;
      border: calc(var(--mg-primary-button-border) * 1px) solid var(--mg-primary-button-background-color);
    }
    &:hover:not(:disabled) .mg-btn-face,
    &:hover:not(:disabled) .mg-btn-label,
    &:hover:not(:disabled) .mg-btn-icon {
      color: var(--mg-primary-button-background-color) !important;
    }
  }

  .mg-btn-secondary {
    background-color: transparent;
    color: var(--mg-secondary-button-text-color);
    border: calc(var(--mg-secondary-button-border) * 1px) solid rgba(var(--mg-secondary-button-text-color-rgb), 0.4);
    border-radius: calc(var(--mg-secondary-button-border-radius) * 1px);
    font-weight: var(--mg-secondary-button-font-weight);

    &:hover:not(:disabled) {
      filter: none;
      background-color: var(--mg-secondary-button-text-color);
      color: white !important;
      border-color: var(--mg-secondary-button-text-color);
    }

    &:hover:not(:disabled) .mg-btn-face,
    &:hover:not(:disabled) .mg-btn-label,
    &:hover:not(:disabled) .mg-btn-icon {
      color: white !important;
    }
  }

  .mg-btn-tertiary {
    background-color: transparent;
    color: var(--mg-secondary-button-text-color);
    border: none;
    border-radius: calc(var(--mg-secondary-button-border-radius) * 1px);
    font-weight: var(--mg-secondary-button-font-weight);

    &:hover:not(:disabled) {
      filter: none;
      background-color: var(--mg-secondary-button-text-color);
      color: white !important;
      border: none;
    }

    &:hover:not(:disabled) .mg-btn-face,
    &:hover:not(:disabled) .mg-btn-label,
    &:hover:not(:disabled) .mg-btn-icon {
      color: white !important;
    }
  }

  .mg-btn.mg-btn--icon-only {
    width: 40px;
    min-width: 40px;
    max-width: 40px;
    padding: 0;
  }
</style>
